import React, { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader, NotFoundException } from '@zxing/library';
import BackgroundLayout from '../components/BackgroundLayout';
import { toast } from 'react-hot-toast';
import { reportError } from '../utils/reportError';

const ScannerPage = () => {
    const [scanning, setScanning] = useState(true);
    const [result, setResult] = useState(null);
    const videoRef = useRef(null);
    const codeReaderRef = useRef(null);

    useEffect(() => {
        const codeReader = new BrowserQRCodeReader();
        codeReaderRef.current = codeReader;

        const startScanning = () => {
            codeReader.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
                if (result) {
                    const code = result.text;

                    // Detener el lector de QR
                    codeReader.reset();

                    // Un solo endpoint: busca el código en citas y en eventos
                    // y responde con `type` para saber qué mostrar.
                    const fetchPromise = fetch('https://admin-django-inspira.azurewebsites.net/api/services/scan-attendance/', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ code }),
                    })
                        .then(async response => {
                            const data = await response.json().catch(() => ({}));
                            if (!response.ok) {
                                // Un 404/400 es normal (código ajeno, ya registrado):
                                // solo reportamos lo que sí es una falla.
                                if (response.status >= 500) {
                                    reportError(`scan-attendance respondió ${response.status}`, {
                                        context: { code, respuesta: data },
                                    });
                                }
                                throw Object.assign(new Error(data.error || 'Ups, no está en la base'), { data });
                            }
                            return data;
                        })
                        .then(data => {
                            setResult(data);
                            setScanning(false);
                            return data.type === 'event'
                                ? '¡Asistencia al evento registrada!'
                                : '¡Asistencia registrada!';
                        })
                        .catch(error => {
                            // Sin `data` es que ni siquiera hubo respuesta: se cayó la red
                            // o el servidor. Eso es justo lo que no queríamos perder.
                            if (!error.data) {
                                reportError(`Falló el escaneo: ${error.message}`, {
                                    stack: error.stack || '',
                                    context: { code },
                                });
                            }
                            setResult({ ...(error.data || {}), error: error.message });
                            setScanning(false);
                            throw error;
                        });

                    toast.promise(fetchPromise, {
                        loading: 'Buscando el código...',
                        success: (message) => message,
                        error: (err) => err.message,
                    });
                }
                // NotFoundException es "no hay QR en el cuadro", pasa todo el tiempo.
                // Lo demás sí es una falla real: cámara sin permiso, sin cámara, etc.
                if (err && !(err instanceof NotFoundException)) {
                    console.error(err);
                    reportError(`Cámara/lector QR: ${err.message || err}`, {
                        stack: err.stack || '',
                        context: { tipo: 'zxing' },
                    });
                }
            });
        };

        startScanning();

        return () => {
            codeReader.reset();
        };
    }, []);

    const handleRestart = () => {
        window.location.reload();
    };

    const hasError = result && result.error;

    return (
        <BackgroundLayout className="px-8 pb-24 overflow-hidden">
            {scanning ? (
                <>
                    <h1 className='text-6xl font-bold text-[#f8485e] mt-8 drop-shadow-lg'>👀</h1>
                    <h1 className="text-xl font-bold mt-4 text-center text-gray-800 mb-6 drop-shadow uppercase">¿Quién nos visita hoy?</h1>
                    <div className="w-[480px] max-w-[90vw] aspect-square relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#f8485e] mx-auto">
                        <video ref={videoRef} id="interactive" className="w-full h-full object-cover"></video>
                    </div>
                </>
            ) : (
                <div id="result" className="w-full max-w-md mt-4 p-8 bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 flex flex-col items-center justify-center gap-6 animate-in fade-in zoom-in duration-500">
                    {hasError ? (
                        <>
                            <p className="text-red-500 font-bold uppercase text-center">{result.error}</p>
                            {/* En "ya registrada" el backend igual dice de quién es */}
                            {result.nickname && (
                                <p className="text-sm font-bold text-gray-500 uppercase tracking-widest text-center">
                                    {result.nickname}{result.curp_ref ? ` (${result.curp_ref})` : ''} · {result.attendance_datetime}
                                </p>
                            )}
                        </>
                    ) : result.type === 'event' ? (
                        <>
                            <h1 className="text-3xl font-black text-[#f8485e] text-center uppercase tracking-tight">Gracias por asistir {result.nickname} 💖</h1>
                            {result.curp_ref && (
                                <p className="-mt-4 text-xs font-bold text-gray-400 uppercase tracking-[0.3em]">Ref: {result.curp_ref}</p>
                            )}
                            <h1 className="text-xl font-bold text-gray-800 uppercase tracking-wide text-center">{result.event_title}</h1>
                            <div className="text-center">
                                <h1 className="text-xs font-bold text-gray-500 uppercase mb-1">Fecha y hora de asistencia:</h1>
                                <h1 className="text-lg font-bold text-gray-900">{result.attendance_datetime}</h1>
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className="text-3xl font-black text-[#f8485e] text-center uppercase tracking-tight">Bienvenidx {result.nickname} 🫶🏻</h1>
                            {result.curp_ref && (
                                <p className="-mt-4 text-xs font-bold text-gray-400 uppercase tracking-[0.3em]">Ref: {result.curp_ref}</p>
                            )}
                            <div className={`rounded-full px-6 py-2 shadow-lg ${result.punctuality === 'A TIEMPO' ? 'bg-green-500 shadow-green-500/30' : 'bg-red-500 shadow-red-500/30'}`}>
                                <h1 className="text-lg font-bold text-white uppercase tracking-wider">Puntualidad: {result.punctuality}</h1>
                            </div>
                            <h1 className="text-xl font-bold text-gray-800 uppercase tracking-wide">{result.service}</h1>
                            <div className="text-center">
                                <h1 className="text-sm font-bold text-gray-500 uppercase mb-1">Fecha y hora de la cita:</h1>
                                <h1 className="text-2xl font-black text-gray-900">{result.date} <span className="text-[#f8485e]">{result.time}</span></h1>
                            </div>
                            <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mt-2">Estado: <span className="text-gray-900">{result.status}</span></p>
                        </>
                    )}

                    <button onClick={handleRestart} className="bg-[#f8485e] hover:bg-[#d93a4f] text-white py-4 px-6 rounded-xl shadow-lg mt-4 w-full font-bold uppercase transition-all active:scale-95">Escanear Otro QR</button>
                </div>
            )}
            <div id="status" className="text-center font-bold mt-4 text-[#f8485e] uppercase text-xs tracking-widest"></div>
        </BackgroundLayout>
    );
};

export default ScannerPage;
